import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { ClipboardList, Link, Upload, Loader2, X, ExternalLink, Check, Trash2, FileText } from 'lucide-react';
import { apiRequest } from '@/utils/api-request';
import { showToast } from '@/components/ui/Toast';
import Cookies from 'js-cookie';

interface QuestionnaireData {
    questionnaire_link: string | null;
    questionnaire_file: string | null;
    questionnaire_file_name: string | null;
}

const Questionnaire: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<QuestionnaireData>({
        questionnaire_link: null,
        questionnaire_file: null,
        questionnaire_file_name: null,
    });

    // Link state
    const [linkInput, setLinkInput] = useState('');
    const [savingLink, setSavingLink] = useState(false);

    // File state
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploadingFile, setUploadingFile] = useState(false);
    const [removingFile, setRemovingFile] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        fetchData();
    }, [id]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await apiRequest(`/questionnaire/${id}`);
            if (response.success) {
                setData(response.data);
                setLinkInput(response.data.questionnaire_link || '');
            } else {
                showToast(response.message || 'Failed to load questionnaire', 'error');
            }
        } catch (err) {
            showToast('Failed to load questionnaire', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveLink = async () => {
        try {
            setSavingLink(true);
            const response = await apiRequest(`/questionnaire/${id}/link`, {
                method: 'PUT',
                body: { link: linkInput.trim() },
            });
            if (response.success) {
                setData(prev => ({ ...prev, questionnaire_link: linkInput.trim() || null }));
                showToast(linkInput.trim() ? 'Link saved!' : 'Link removed!', 'success');
            } else {
                showToast(response.message || 'Failed to save link', 'error');
            }
        } catch (err: any) {
            showToast(err.message || 'Failed to save link', 'error');
        } finally {
            setSavingLink(false);
        }
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) setSelectedFile(file);
    };

    const handleUploadFile = async () => {
        if (!selectedFile) return;
        try {
            setUploadingFile(true);
            const formData = new FormData();
            formData.append('file', selectedFile);

            const token = Cookies.get('crm_auth_token');
            const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

            const response = await fetch(`${API_BASE_URL}/questionnaire/${id}/file`, {
                method: 'POST',
                headers: token ? { Authorization: `Bearer ${token}` } : {},
                credentials: 'include',
                body: formData,
            });

            const result = await response.json();

            if (response.ok && result.success) {
                setData(prev => ({
                    ...prev,
                    questionnaire_file: result.data.questionnaire_file,
                    questionnaire_file_name: result.data.questionnaire_file_name,
                }));
                setSelectedFile(null);
                if (fileInputRef.current) fileInputRef.current.value = '';
                showToast('File uploaded successfully!', 'success');
            } else {
                showToast(result.message || 'Failed to upload file', 'error');
            }
        } catch (err: any) {
            showToast(err.message || 'Failed to upload file', 'error');
        } finally {
            setUploadingFile(false);
        }
    };

    const handleRemoveFile = async () => {
        if (!confirm('Remove the uploaded file?')) return;
        try {
            setRemovingFile(true);
            const response = await apiRequest(`/questionnaire/${id}/file`, { method: 'DELETE' });
            if (response.success) {
                setData(prev => ({ ...prev, questionnaire_file: null, questionnaire_file_name: null }));
                showToast('File removed!', 'success');
            } else {
                showToast(response.message || 'Failed to remove file', 'error');
            }
        } catch (err: any) {
            showToast(err.message || 'Failed to remove file', 'error');
        } finally {
            setRemovingFile(false);
        }
    };

    const isLinkChanged = linkInput.trim() !== (data.questionnaire_link || '');

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <Loader2 className="h-8 w-8 text-brandColor animate-spin" />
            </div>
        );
    }

    return (
        <div className="h-full bg-white dark:bg-gray-900">
            {/* Header */}
            <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-8 py-4">
                <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                        <div className="p-2 bg-brandColor rounded-lg text-white">
                            <ClipboardList className="w-6 h-6" />
                        </div>
                    </div>
                    <div>
                        <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Blog Questionnaire</h1>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Add a questionnaire link or upload a file for blog content</p>
                    </div>
                </div>
            </div>

            <div className="p-8 space-y-8">

                {/* Type 1 — Link */}
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="bg-brandColor px-6 py-3">
                        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                            <Link className="w-5 h-5" />
                            Questionnaire Link
                        </h2>
                        <p className="text-xs text-white/80 mt-1">Add a link to an online questionnaire (Google Forms, Typeform, etc.)</p>
                    </div>
                    <div className="p-6 space-y-3">
                        <div className="flex gap-3">
                            <input
                                type="url"
                                value={linkInput}
                                onChange={(e) => setLinkInput(e.target.value)}
                                placeholder="https://forms.google.com/..."
                                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor focus:border-brandColor text-sm"
                                onKeyPress={(e) => { if (e.key === 'Enter') handleSaveLink(); }}
                            />
                            <button
                                onClick={handleSaveLink}
                                disabled={savingLink || !isLinkChanged}
                                className="inline-flex items-center gap-2 px-4 py-2 bg-brandColor hover:bg-brand-hover text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                            >
                                {savingLink ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                                {savingLink ? 'Saving...' : 'Save'}
                            </button>
                            {linkInput.trim() && (
                                <button
                                    onClick={() => { setLinkInput(''); }}
                                    className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                                    title="Clear"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                        </div>

                        {data.questionnaire_link && !isLinkChanged && (
                            <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                                <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                                <a
                                    href={data.questionnaire_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-green-700 dark:text-green-400 hover:underline truncate flex-1"
                                >
                                    {data.questionnaire_link}
                                </a>
                                <a
                                    href={data.questionnaire_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex-shrink-0 text-green-600 hover:text-green-800"
                                >
                                    <ExternalLink className="w-4 h-4" />
                                </a>
                            </div>
                        )}
                    </div>
                </div>

                {/* Type 2 — File Upload */}
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="bg-brandColor px-6 py-3">
                        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                            <Upload className="w-5 h-5" />
                            Upload Questionnaire File
                        </h2>
                        <p className="text-xs text-white/80 mt-1">Upload a questionnaire document (PDF, DOC, DOCX, XLS, XLSX, TXT, or any file — max 20MB)</p>
                    </div>
                    <div className="p-6 space-y-4">

                        {/* Currently saved file */}
                        {data.questionnaire_file && (
                            <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                    <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-blue-800 dark:text-blue-300 truncate">
                                            {data.questionnaire_file_name || 'Uploaded file'}
                                        </p>
                                        <p className="text-xs text-blue-600 dark:text-blue-400">Current file</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 ml-3">
                                    <a
                                        href={data.questionnaire_file}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="p-1.5 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900 rounded transition-colors"
                                        title="Open file"
                                    >
                                        <ExternalLink className="w-4 h-4" />
                                    </a>
                                    <button
                                        onClick={handleRemoveFile}
                                        disabled={removingFile}
                                        className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-50"
                                        title="Remove file"
                                    >
                                        {removingFile ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* File picker */}
                        <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 dark:border-gray-600 transition-colors">
                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                <Upload className="w-8 h-8 mb-2 text-gray-400" />
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    <span className="font-semibold">Click to select</span> or drag and drop
                                </p>
                                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">PDF, DOC, DOCX, XLS, XLSX, TXT, or any file (max 20MB)</p>
                            </div>
                            <input
                                ref={fileInputRef}
                                type="file"
                                className="hidden"
                                accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.ppt,.pptx"
                                onChange={handleFileSelect}
                            />
                        </label>

                        {/* Selected file preview + upload button */}
                        {selectedFile && (
                            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                    <FileText className="w-5 h-5 text-gray-500 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-800 dark:text-white truncate">{selectedFile.name}</p>
                                        <p className="text-xs text-gray-500">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 ml-3">
                                    <button
                                        onClick={handleUploadFile}
                                        disabled={uploadingFile}
                                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-brandColor hover:bg-brand-hover text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {uploadingFile ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                                        {uploadingFile ? 'Uploading...' : 'Upload'}
                                    </button>
                                    <button
                                        onClick={() => { setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                                        className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default Questionnaire;
